import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { ButtonToggle, DictionaryItem } from '@hglobal/common';
import {
  AchievementStatus,
  AchievementType,
  AutomaticCongratsType,
  BlogStatus,
  CourseRequestStatus,
  CourseStatusType,
  CourseType,
  DeliveryMethod,
  FinancialRequestStatus,
  GroupTagEnum,
  LimitApprovalStatus,
  ManualCongratsType,
  MessageStatus,
  Messengers,
  OfficeVisitsStatuses,
  OfficeVisitsTypes,
  QuestStatusType,
  SlackChannelTypeEnum,
  SlackWorkspaces,
  SortType,
  SubordinatesLevels,
  SubordinatesTypes,
  SupportRequestCategoryType,
  SupportRequestStatusType,
  SurveyStatus,
  TgChatPermissions,
  TgChatTypes,
  TgUserPermissions,
  TransactionDirection,
  UserRoles,
  UserStatusType,
  VacationPlannerEventType,
  VacationPlannerViewType,
  VotesAndSurveysStatus,
  WorkingMessengerLinks,
} from '@pt/models';

import {
  MentoringPlanStatus,
} from '../../../../../apps/site/src/app/pages/my-team/pages/mentorship/models';

@Injectable({
  providedIn: 'root',
})
export class DictionariesService {
  deliveryMethods: DeliveryMethod[] = [
    { id: 'international', name: this._translate.instant('dictionary.deliveries.international') },
    { id: 'ukrposhta', name: this._translate.instant('dictionary.deliveries.ukrposhta') },
    { id: 'nova_poshta', name: this._translate.instant('dictionary.deliveries.novaPoshta') },
    { id: 'fedex', name: this._translate.instant('dictionary.deliveries.fedex') },
    { id: 'dhl', name: this._translate.instant('dictionary.deliveries.dhl') },
    { id: 'dpd', name: this._translate.instant('dictionary.deliveries.dpd') },
    { id: 'inpost', name: this._translate.instant('dictionary.deliveries.inpost') },
    { id: 'acs', name: this._translate.instant('dictionary.deliveries.acs') },
    {
      id: 'gap_akis_express',
      name: this._translate.instant('dictionary.deliveries.gapAkisExpress'),
    },
  ];

  languages: DictionaryItem<string>[] = [
    {
      id: 'EN',
      name: this._translate.instant('dictionary.languages.en'),
    },
    {
      id: 'RU',
      name: this._translate.instant('dictionary.languages.ru'),
    },
    {
      id: 'UA',
      name: this._translate.instant('dictionary.languages.ua'),
    },
    {
      id: 'KZ',
      name: this._translate.instant('dictionary.languages.kz'),
    },
    {
      id: 'PL',
      name: this._translate.instant('dictionary.languages.pl'),
    },
    {
      id: 'AM',
      name: this._translate.instant('dictionary.languages.am'),
    },
    {
      id: 'ES',
      name: this._translate.instant('dictionary.languages.es'),
    },
  ];

  orderStatuses: DictionaryItem<string>[] = [
    { id: null, name: this._translate.instant('dictionary.orderStatuses.all') },
    { id: 'inReview', name: this._translate.instant('dictionary.orderStatuses.inReview') },
    { id: 'rejected', name: this._translate.instant('dictionary.orderStatuses.rejected') },
    { id: 'new', name: this._translate.instant('dictionary.orderStatuses.new') },
    { id: 'confirmed', name: this._translate.instant('dictionary.orderStatuses.confirmed') },
    { id: 'shipping', name: this._translate.instant('dictionary.orderStatuses.shipping') },
    { id: 'delivered', name: this._translate.instant('dictionary.orderStatuses.delivered') },
    { id: 'completed', name: this._translate.instant('dictionary.orderStatuses.completed') },
    { id: 'canceled', name: this._translate.instant('dictionary.orderStatuses.canceled') },
  ];

  questStatuses: DictionaryItem<QuestStatusType>[] = [
    { id: null, name: this._translate.instant('dictionary.questStatuses.all') },
    { id: 'inProgress', name: this._translate.instant('dictionary.questStatuses.inProgress') },
    { id: 'inReview', name: this._translate.instant('dictionary.questStatuses.inReview') },
    { id: 'completed', name: this._translate.instant('dictionary.questStatuses.completed') },
    { id: 'rejected', name: this._translate.instant('dictionary.questStatuses.rejected') },
    { id: 'canceled', name: this._translate.instant('dictionary.questStatuses.canceled') },
  ];

  supportRequestStatuses: DictionaryItem<SupportRequestStatusType>[] = [
    {
      id: SupportRequestStatusType.New,
      name: this._translate.instant('dictionary.supportRequestStatuses.new'),
    },
    {
      id: SupportRequestStatusType.InProgress,
      name: this._translate.instant('dictionary.supportRequestStatuses.inProgress'),
    },
    {
      id: SupportRequestStatusType.Completed,
      name: this._translate.instant('dictionary.supportRequestStatuses.completed'),
    },
  ];

  courseStatuses: DictionaryItem<CourseStatusType>[] = [
    {
      id: CourseStatusType.InProgress,
      name: this._translate.instant('dictionary.courseStatuses.inProgress'),
    },
    {
      id: CourseStatusType.Completed,
      name: this._translate.instant('dictionary.courseStatuses.completed'),
    },
    {
      id: CourseStatusType.NotPassed,
      name: this._translate.instant('dictionary.courseStatuses.notPassed'),
    },
  ];

  courseTypes: DictionaryItem<CourseType>[] = [
    { id: CourseType.Mandatory, name: this._translate.instant('dictionary.courseTypes.mandatory') },
    { id: CourseType.Open, name: this._translate.instant('dictionary.courseTypes.open') },
    { id: CourseType.Request, name: this._translate.instant('dictionary.courseTypes.request') },
  ];

  messageStatuses: DictionaryItem<MessageStatus>[] = [
    {
      id: MessageStatus.Scheduled,
      name: this._translate.instant('dictionary.messageStatuses.scheduled'),
    },
    { id: MessageStatus.Sent, name: this._translate.instant('dictionary.messageStatuses.sent') },
  ];

  periods: DictionaryItem<number>[] = [
    { id: null, name: this._translate.instant('dictionary.periods.all') },
    { id: 1, name: this._translate.instant('dictionary.periods.month') },
    { id: 3, name: this._translate.instant('dictionary.periods.threeMonths') },
    { id: 6, name: this._translate.instant('dictionary.periods.sixMonths') },
    { id: 12, name: this._translate.instant('dictionary.periods.year') },
  ];

  supportRequestCategories: DictionaryItem<SupportRequestCategoryType>[] = [
    {
      id: SupportRequestCategoryType.Office,
      name: this._translate.instant('dictionary.supportCategories.office'),
      code: 'AdminSupportOffice',
    },
    {
      id: SupportRequestCategoryType.Education,
      name: this._translate.instant('dictionary.supportCategories.education'),
      code: 'AdminSupportEducation',
    },
    {
      id: SupportRequestCategoryType.Platform,
      name: this._translate.instant('dictionary.supportCategories.platform'),
      code: 'AdminSupportPlatform',
    },
  ];

  events: DictionaryItem<string>[] = [
    { id: 'new_account', name: this._translate.instant('dictionary.events.newAccount') },
    { id: 'probation_end', name: this._translate.instant('dictionary.events.probationEnd') },
    { id: 'birthday', name: this._translate.instant('dictionary.events.birthday') },
    {
      id: 'personal_year_start',
      name: this._translate.instant('dictionary.events.personalYearStart'),
    },
  ];

  months: DictionaryItem<number>[] = [
    { id: 1, name: this._translate.instant('dictionary.months.january') },
    { id: 2, name: this._translate.instant('dictionary.months.february') },
    { id: 3, name: this._translate.instant('dictionary.months.march') },
    { id: 4, name: this._translate.instant('dictionary.months.april') },
    { id: 5, name: this._translate.instant('dictionary.months.may') },
    { id: 6, name: this._translate.instant('dictionary.months.june') },
    { id: 7, name: this._translate.instant('dictionary.months.july') },
    { id: 8, name: this._translate.instant('dictionary.months.august') },
    { id: 9, name: this._translate.instant('dictionary.months.september') },
    { id: 10, name: this._translate.instant('dictionary.months.october') },
    { id: 11, name: this._translate.instant('dictionary.months.november') },
    { id: 12, name: this._translate.instant('dictionary.months.december') },
  ];

  monthsKeys = [
    'dictionary.months.january',
    'dictionary.months.february',
    'dictionary.months.march',
    'dictionary.months.april',
    'dictionary.months.may',
    'dictionary.months.june',
    'dictionary.months.july',
    'dictionary.months.august',
    'dictionary.months.september',
    'dictionary.months.october',
    'dictionary.months.november',
    'dictionary.months.december',
  ];

  automaticAccrual: DictionaryItem<string>[] = [
    {
      id: 'monthly-group-tasks',
      name: this._translate.instant('admin.coins.automaticAccrual.table.type.monthlyGroup'),
      description: this._translate.instant(
        'admin.coins.automaticAccrual.table.description.automaticAccrueMonthly',
      ),
    },
    {
      id: 'yearly-group-tasks',
      name: this._translate.instant('admin.coins.automaticAccrual.table.type.yearlyGroup'),
      description: this._translate.instant(
        'admin.coins.automaticAccrual.table.description.automaticAccrueYearly',
      ),
    },
    {
      id: 'monthly-user-tasks',
      name: this._translate.instant('admin.coins.automaticAccrual.table.type.user'),
      description: this._translate.instant(
        'admin.coins.automaticAccrual.table.description.assingAnAward',
      ),
    },
  ];

  automaticTemplates: DictionaryItem<string>[] = [
    {
      id: 'birthday',
      name: this._translate.instant('admin.templates.automatic.table.type.birthday'),
    },
    {
      id: 'anniversary',
      name: this._translate.instant('admin.templates.automatic.table.type.anniversary'),
    },
    {
      id: 'newcomers',
      name: this._translate.instant('admin.templates.automatic.table.type.newcomers'),
    },
    {
      id: 'probation-end',
      name: this._translate.instant('admin.templates.automatic.table.type.probationEnd'),
    },
  ];

  manualTemplates: DictionaryItem<string>[] = [
    {
      id: 'wedding',
      name: this._translate.instant('admin.templates.manual.table.type.wedding'),
    },
    {
      id: 'birth-child',
      name: this._translate.instant('admin.templates.manual.table.type.birthChild'),
    },
    {
      id: 'promotion',
      name: this._translate.instant('admin.templates.manual.table.type.promotion'),
    },
  ];

  automaticCongratsTypes: DictionaryItem<AutomaticCongratsType>[] = [
    { id: 'birthday', name: this._translate.instant('dictionary.automaticCongratsTypes.birthday') },
    {
      id: 'personalYearStart',
      name: this._translate.instant('dictionary.automaticCongratsTypes.personalYearStart'),
    },
    {
      id: 'probationEnd',
      name: this._translate.instant('dictionary.automaticCongratsTypes.probationEnd'),
    },
  ];

  manualCongratsTypes: DictionaryItem<ManualCongratsType>[] = [
    { id: 'wedding', name: this._translate.instant('dictionary.manualCongratsTypes.wedding') },
    {
      id: 'birthChild',
      name: this._translate.instant('dictionary.manualCongratsTypes.birthChild'),
    },
    { id: 'promotion', name: this._translate.instant('dictionary.manualCongratsTypes.promotion') },
  ];

  templateTypes: DictionaryItem<string>[] = [
    ...this.automaticCongratsTypes,
    ...this.manualCongratsTypes,
    { id: 'newcomers', name: this._translate.instant('dictionary.templateTypes.newcomers') },
  ];

  approvalStatuses: DictionaryItem<LimitApprovalStatus>[] = [
    { id: null, name: this._translate.instant('dictionary.approvalStatuses.all') },
    { id: 'auto', name: this._translate.instant('dictionary.approvalStatuses.auto') },
    { id: 'rejected', name: this._translate.instant('dictionary.approvalStatuses.rejected') },
    { id: 'inReview', name: this._translate.instant('dictionary.approvalStatuses.inReview') },
    { id: 'approved', name: this._translate.instant('dictionary.approvalStatuses.approved') },
  ];

  achievementTypes: DictionaryItem<AchievementType>[] = [
    {
      id: AchievementType.Unique,
      name: this._translate.instant('dictionary.achievementTypes.unique'),
    },
    {
      id: AchievementType.Colleagues,
      name: this._translate.instant('dictionary.achievementTypes.colleagues'),
    },
    {
      id: AchievementType.Managers,
      name: this._translate.instant('dictionary.achievementTypes.managers'),
    },
    {
      id: AchievementType.Education,
      name: this._translate.instant('dictionary.achievementTypes.education'),
    },
    {
      id: AchievementType.Onboarding,
      name: this._translate.instant('dictionary.achievementTypes.onboarding'),
    },
    {
      id: AchievementType.Surveys,
      name: this._translate.instant('dictionary.achievementTypes.surveys'),
    },
  ];

  achievementStatuses: DictionaryItem<AchievementStatus>[] = [
    {
      id: AchievementStatus.Sent,
      name: this._translate.instant('dictionary.achievementStatuses.sent'),
    },
    {
      id: AchievementStatus.Received,
      name: this._translate.instant('dictionary.achievementStatuses.received'),
    },
  ];

  userRoles: DictionaryItem<UserRoles>[] = [
    { id: UserRoles.User, name: this._translate.instant('dictionary.userRoles.user') },
    { id: UserRoles.Admin, name: this._translate.instant('dictionary.userRoles.admin') },
  ];

  tgChatPermissions: DictionaryItem<TgChatPermissions>[] = [
    {
      id: TgChatPermissions.Member,
      name: this._translate.instant('dictionary.tgChatPermissions.member'),
    },
    {
      id: TgChatPermissions.Admin,
      name: this._translate.instant('dictionary.tgChatPermissions.admin'),
    },
  ];

  tgUserPermissions: DictionaryItem<TgUserPermissions>[] = [
    {
      id: TgUserPermissions.Member,
      name: this._translate.instant('dictionary.tgUserPermissions.member'),
    },
    {
      id: TgUserPermissions.Owner,
      name: this._translate.instant('dictionary.tgUserPermissions.owner'),
    },
    {
      id: TgUserPermissions.Admin,
      name: this._translate.instant('dictionary.tgUserPermissions.admin'),
    },
  ];

  tgChatTypes: DictionaryItem<TgChatTypes>[] = [
    {
      id: TgChatTypes.Channel,
      name: this._translate.instant('dictionary.tgChatTypes.channel'),
    },
    {
      id: TgChatTypes.Bot,
      name: this._translate.instant('dictionary.tgChatTypes.bot'),
    },
    {
      id: TgChatTypes.User,
      name: this._translate.instant('dictionary.tgChatTypes.user'),
    },
  ];

  userStatuses: DictionaryItem<UserStatusType>[] = [
    { id: 'active', name: this._translate.instant('dictionary.userStatuses.active') },
    { id: 'probation', name: this._translate.instant('dictionary.userStatuses.probation') },
    { id: 'offboarding', name: this._translate.instant('dictionary.userStatuses.offboarding') },
  ];

  slackWorkspaces: DictionaryItem<SlackWorkspaces>[] = [
    { id: SlackWorkspaces.RedCore, name: SlackWorkspaces.RedCore },
    { id: SlackWorkspaces.TWU, name: SlackWorkspaces.TWU },
  ];

  channelTypes: DictionaryItem<SlackChannelTypeEnum>[] = [
    {
      id: SlackChannelTypeEnum.News,
      name: this._translate.instant('dictionary.channelTypes.news'),
    },
    {
      id: SlackChannelTypeEnum.Team,
      name: this._translate.instant('dictionary.channelTypes.team'),
    },
    {
      id: SlackChannelTypeEnum.Club,
      name: this._translate.instant('dictionary.channelTypes.club'),
    },
    {
      id: SlackChannelTypeEnum.Office,
      name: this._translate.instant('dictionary.channelTypes.office'),
    },
    {
      id: SlackChannelTypeEnum.Community,
      name: this._translate.instant('dictionary.channelTypes.community'),
    },
    {
      id: SlackChannelTypeEnum.Finance,
      name: this._translate.instant('dictionary.channelTypes.finance'),
    },
    {
      id: SlackChannelTypeEnum.Messages,
      name: this._translate.instant('dictionary.channelTypes.messages'),
    },
    {
      id: SlackChannelTypeEnum.Hr,
      name: this._translate.instant('dictionary.channelTypes.hr'),
    },
  ];

  sortTypes: SortType[] = [
    { id: 'id', name: this._translate.instant('dictionary.sortTypes.id') },
    { id: '-id', name: this._translate.instant('dictionary.sortTypes.idDesc') },
    { id: 'name', name: this._translate.instant('dictionary.sortTypes.name') },
    { id: '-name', name: this._translate.instant('dictionary.sortTypes.nameDesc') },
    { id: 'login', name: this._translate.instant('dictionary.sortTypes.login') },
    { id: '-login', name: this._translate.instant('dictionary.sortTypes.loginDesc') },
    { id: 'date', name: this._translate.instant('dictionary.sortTypes.date') },
    { id: '-date', name: this._translate.instant('dictionary.sortTypes.dateDesc') },
    { id: 'title', name: this._translate.instant('dictionary.sortTypes.title') },
    { id: '-title', name: this._translate.instant('dictionary.sortTypes.titleDesc') },
    { id: 'price', name: this._translate.instant('dictionary.sortTypes.price') },
    { id: '-price', name: this._translate.instant('dictionary.sortTypes.priceDesc') },
    { id: 'reward', name: this._translate.instant('dictionary.sortTypes.reward') },
    { id: '-reward', name: this._translate.instant('dictionary.sortTypes.rewardDesc') },
    { id: 'members', name: this._translate.instant('dictionary.sortTypes.members') },
    { id: '-members', name: this._translate.instant('dictionary.sortTypes.membersDesc') },
    { id: 'new', name: this._translate.instant('dictionary.sortTypes.new') },
    { id: 'sale', name: this._translate.instant('dictionary.sortTypes.sale') },
    { id: 'mostRecently', name: this._translate.instant('dictionary.sortTypes.mostRecently') },
    { id: 'brandName', name: this._translate.instant('dictionary.sortTypes.brand') },
    { id: '-brandName', name: this._translate.instant('dictionary.sortTypes.brandDesc') },
  ];

  workingMessengers: DictionaryItem<string>[] = [
    { id: Messengers.Telegram, name: 'Telegram' },
    { id: Messengers.Instagram, name: 'Instagram' },
    { id: Messengers.WhatsApp, name: 'WhatsApp' },
    { id: Messengers.LinkedIn, name: 'LinkedIn' },
    { id: Messengers.FaceBook, name: 'Facebook' },
  ];

  workingMessengerLinks: WorkingMessengerLinks = {
    [Messengers.Telegram]: {
      prefix: 'https://www.telegram.me/',
    },
    [Messengers.Instagram]: {
      prefix: 'https://www.instagram.com/',
    },
    [Messengers.WhatsApp]: {
      prefix: 'https://wa.me/',
    },
    [Messengers.FaceBook]: {
      prefix: '',
      substringPrefix: 'https://www.facebook.com/',
    },
    [Messengers.LinkedIn]: {
      prefix: '',
      substringPrefix: 'https://www.linkedin.com/in/',
    },
  };

  modules: DictionaryItem<GroupTagEnum>[] = [
    { id: GroupTagEnum.Clubs, name: this._translate.instant('dictionary.groupTags.clubs') },
    { id: GroupTagEnum.Coins, name: this._translate.instant('dictionary.groupTags.normalCoins') },
    {
      id: GroupTagEnum.Communities,
      name: this._translate.instant('dictionary.groupTags.communities'),
    },
    { id: GroupTagEnum.Congrats, name: this._translate.instant('dictionary.groupTags.congrats') },
    {
      id: GroupTagEnum.CorpOnboarding,
      name: this._translate.instant('dictionary.groupTags.corpOnboarding'),
    },
    { id: GroupTagEnum.Courses, name: this._translate.instant('dictionary.groupTags.courses') },
    {
      id: GroupTagEnum.CoursesNotification,
      name: this._translate.instant('dictionary.groupTags.coursesNotification'),
    },
    {
      id: GroupTagEnum.EducationEvents,
      name: this._translate.instant('dictionary.groupTags.educationEvents'),
    },
    { id: GroupTagEnum.FAQ, name: this._translate.instant('dictionary.groupTags.faq') },
    { id: GroupTagEnum.Finances, name: this._translate.instant('dictionary.groupTags.finance') },
    { id: GroupTagEnum.Limits, name: this._translate.instant('dictionary.groupTags.limits') },
    {
      id: GroupTagEnum.MediaLibrary,
      name: this._translate.instant('dictionary.groupTags.mediaLibrary'),
    },
    { id: GroupTagEnum.Messages, name: this._translate.instant('dictionary.groupTags.messages') },
    { id: GroupTagEnum.News, name: this._translate.instant('dictionary.groupTags.news') },
    { id: GroupTagEnum.Survey, name: this._translate.instant('dictionary.groupTags.survey') },
  ];

  surveyStatuses: DictionaryItem<SurveyStatus>[] = [
    {
      id: SurveyStatus.PLANNED,
      name: this._translate.instant('dictionary.surveyStatuses.planned'),
    },
    {
      id: SurveyStatus.IN_PROGRESS,
      name: this._translate.instant('dictionary.surveyStatuses.inProgress'),
    },
    {
      id: SurveyStatus.FINISHED,
      name: this._translate.instant('dictionary.surveyStatuses.finished'),
    },
  ];

  blogStatuses: DictionaryItem<BlogStatus>[] = [
    {
      id: BlogStatus.InReview,
      name: this._translate.instant('dictionary.blogStatuses.inReview'),
    },
    {
      id: BlogStatus.Approved,
      name: this._translate.instant('dictionary.blogStatuses.approved'),
    },
  ];

  usersCourseStatuses: DictionaryItem<CourseStatusType>[] = [
    {
      id: CourseStatusType.NotPassed,
      name: this._translate.instant('dictionary.usersCourseStatuses.notPassed'),
    },
    {
      id: CourseStatusType.InProgress,
      name: this._translate.instant('dictionary.usersCourseStatuses.inProgress'),
    },
    {
      id: CourseStatusType.Completed,
      name: this._translate.instant('dictionary.usersCourseStatuses.completed'),
    },
    {
      id: CourseStatusType.NotStarted,
      name: this._translate.instant('dictionary.usersCourseStatuses.notStarted'),
    },
  ];

  courseRequestStatuses: DictionaryItem<CourseRequestStatus>[] = [
    {
      id: CourseRequestStatus.InReview,
      name: this._translate.instant('dictionary.courseRequestStatuses.inReview'),
    },
    {
      id: CourseRequestStatus.Approved,
      name: this._translate.instant('dictionary.courseRequestStatuses.approved'),
    },
    {
      id: CourseRequestStatus.Rejected,
      name: this._translate.instant('dictionary.courseRequestStatuses.rejected'),
    },
  ];

  subordinatesTypes: DictionaryItem<SubordinatesTypes>[] = [
    {
      id: 'direct',
      name: this._translate.instant('dictionary.subordinateType.direct'),
    },
    {
      id: 'functional',
      name: this._translate.instant('dictionary.subordinateType.functional'),
    },
  ];

  subordinatesLevels: DictionaryItem<SubordinatesLevels>[] = [
    {
      id: 'proximate',
      name: this._translate.instant('dictionary.subordinateLevel.proximate'),
    },
    {
      id: 'total',
      name: this._translate.instant('dictionary.subordinateLevel.total'),
    },
  ];

  officeVisitsTypes: DictionaryItem<OfficeVisitsTypes>[] = [
    {
      id: 'vacationPaid',
      name: this._translate.instant('dictionary.officeVisitsTypes.vacationPaid'),
    },
    {
      id: 'vacationUnpaid',
      name: this._translate.instant('dictionary.officeVisitsTypes.vacationUnpaid'),
    },
    {
      id: 'sickLeavePaid',
      name: this._translate.instant('dictionary.officeVisitsTypes.sickLeavePaid'),
    },
    {
      id: 'sickLeaveUnpaid',
      name: this._translate.instant('dictionary.officeVisitsTypes.sickLeaveUnpaid'),
    },
    {
      id: 'dayOff',
      name: this._translate.instant('dictionary.officeVisitsTypes.dayOff'),
    },
    {
      id: 'familyDay',
      name: this._translate.instant('dictionary.officeVisitsTypes.familyDay'),
    },
    {
      id: 'recoveryDayOff',
      name: this._translate.instant('dictionary.officeVisitsTypes.recoveryDayOff'),
    },
    {
      id: 'workFromHome',
      name: this._translate.instant('dictionary.officeVisitsTypes.workFromHome'),
    },
    {
      id: 'businessTrip',
      name: this._translate.instant('dictionary.officeVisitsTypes.businessTrip'),
    },
    {
      id: 'corporateEvent',
      name: this._translate.instant('dictionary.officeVisitsTypes.corporateEvent'),
    },
  ];

  officeVisitsStatuses: DictionaryItem<OfficeVisitsStatuses>[] = [
    {
      id: 'canceled',
      name: this._translate.instant('dictionary.officeVisitsStatuses.canceled'),
    },
    {
      id: 'waitingReport',
      name: this._translate.instant('dictionary.officeVisitsStatuses.waitingReport'),
    },
    {
      id: 'needMoreInfo',
      name: this._translate.instant('dictionary.officeVisitsStatuses.needMoreInfo'),
    },
    {
      id: 'reportProvided',
      name: this._translate.instant('dictionary.officeVisitsStatuses.reportProvided'),
    },
    {
      id: 'closed',
      name: this._translate.instant('dictionary.officeVisitsStatuses.closed'),
    },
    {
      id: 'c-levelReview',
      name: this._translate.instant('dictionary.officeVisitsStatuses.c-levelReview'),
    },
    {
      id: 'approved',
      name: this._translate.instant('dictionary.officeVisitsStatuses.approved'),
    },
    {
      id: 'toApprove',
      name: this._translate.instant('dictionary.officeVisitsStatuses.toApprove'),
    },
    {
      id: 'review',
      name: this._translate.instant('dictionary.officeVisitsStatuses.review'),
    },
    {
      id: 'rejected',
      name: this._translate.instant('dictionary.officeVisitsStatuses.rejected'),
    },
    {
      id: 'inactive',
      name: this._translate.instant('dictionary.officeVisitsStatuses.inactive'),
    },
  ];

  financialRequestStatuses: DictionaryItem<FinancialRequestStatus>[] = [
    {
      id: FinancialRequestStatus.New,
      name: this._translate.instant('dictionary.financialRequestStatuses.new'),
    },
    {
      id: FinancialRequestStatus.InProgress,
      name: this._translate.instant('dictionary.financialRequestStatuses.inProgress'),
    },
    {
      id: FinancialRequestStatus.Completed,
      name: this._translate.instant('dictionary.financialRequestStatuses.completed'),
    },
    {
      id: FinancialRequestStatus.Rejected,
      name: this._translate.instant('dictionary.financialRequestStatuses.rejected'),
    },
    {
      id: FinancialRequestStatus.Canceled,
      name: this._translate.instant('dictionary.financialRequestStatuses.canceled'),
    },
  ];

  financialRequestStatusesButtonToogleGroup: ButtonToggle<FinancialRequestStatus>[] = [
    {
      id: FinancialRequestStatus.New,
      value: FinancialRequestStatus.New,
      label: this._translate.instant('dictionary.financialRequestStatuses.new'),
      isChecked: false,
      isDisabled: true,
    },
    {
      id: FinancialRequestStatus.InProgress,
      value: FinancialRequestStatus.InProgress,
      label: this._translate.instant('dictionary.financialRequestStatuses.inProgress'),
      isChecked: false,
      isDisabled: true,
    },
    {
      id: FinancialRequestStatus.Completed,
      value: FinancialRequestStatus.Completed,
      label: this._translate.instant('dictionary.financialRequestStatuses.completed'),
      isChecked: false,
      isDisabled: true,
    },
    {
      id: FinancialRequestStatus.Rejected,
      value: FinancialRequestStatus.Rejected,
      label: this._translate.instant('dictionary.financialRequestStatuses.rejected'),
      isChecked: false,
      isDisabled: true,
    },
    {
      id: FinancialRequestStatus.Canceled,
      value: FinancialRequestStatus.Canceled,
      label: this._translate.instant('dictionary.financialRequestStatuses.canceled'),
      isChecked: false,
      isDisabled: true,
    },
  ];

  calendarAbsenceTypes: DictionaryItem<VacationPlannerEventType>[] = [
    {
      id: VacationPlannerEventType.VacationPaid,
      name: this._translate.instant('dictionary.vacationPlanner.planned'),
    },
    {
      id: VacationPlannerEventType.Confirmed,
      name: this._translate.instant('dictionary.vacationPlanner.confirmed'),
    },
    {
      id: VacationPlannerEventType.Holidays,
      name: this._translate.instant('dictionary.vacationPlanner.holidays'),
    },
  ];

  vacationPlannerViewTypes: DictionaryItem<VacationPlannerViewType>[] = [
    {
      id: VacationPlannerViewType.Month,
      name: this._translate.instant('dictionary.vacationPlanner.view.month'),
    },
    {
      id: VacationPlannerViewType.ThreeMonths,
      name: this._translate.instant('dictionary.vacationPlanner.view.threeMonth'),
    },
    {
      id: VacationPlannerViewType.SixMonth,
      name: this._translate.instant('dictionary.vacationPlanner.view.sixMonth'),
    },
    {
      id: VacationPlannerViewType.Year,
      name: this._translate.instant('dictionary.vacationPlanner.view.year'),
    },
  ];

  transactionDirections: DictionaryItem<TransactionDirection>[] = [
    {
      id: TransactionDirection.Income,
      name: this._translate.instant('dictionary.transactionDirections.income'),
    },
    {
      id: TransactionDirection.Expense,
      name: this._translate.instant('dictionary.transactionDirections.expense'),
    },
  ];

  votesAndSurveysStatuses: DictionaryItem<VotesAndSurveysStatus>[] = [
    {
      id: VotesAndSurveysStatus.Open,
      name: this._translate.instant('dictionary.votesAndSurveys.open'),
    },
    {
      id: VotesAndSurveysStatus.Completed,
      name: this._translate.instant('dictionary.votesAndSurveys.completed'),
    },
    {
      id: VotesAndSurveysStatus.Expired,
      name: this._translate.instant('dictionary.votesAndSurveys.expired'),
    },
  ];

  mentoringPlanStages: DictionaryItem<MentoringPlanStatus>[] = [
    {
      id: MentoringPlanStatus.NotAssigned,
      name: this._translate.instant('dictionary.mentoringPlanStage.notAssigned'),
    },
    {
      id: MentoringPlanStatus.NotStarted,
      name: this._translate.instant('dictionary.mentoringPlanStage.notStarted'),
    },
    {
      id: MentoringPlanStatus.InProgress,
      name: this._translate.instant('dictionary.mentoringPlanStage.inProgress'),
    },
    {
      id: MentoringPlanStatus.Finished,
      name: this._translate.instant('dictionary.mentoringPlanStage.finished'),
    },
  ];

  constructor(private _translate: TranslateService) {
  }

  filterSortTypes(...ids: string[]): SortType[] {
    const doubleIds = [...ids, ...ids.map(id => `-${id}`)];

    return this.sortTypes.filter(item => doubleIds.includes(item.id));
  }
}
